import { STORAGE_KEY } from "@constants/misc";
import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { MemberSettings } from "@types";
import {
  clearDepositTracking,
  getPersistedDepositTracking,
  persistDepositTracking,
} from "@utils/deposit-tracking-persistence";
import { buildMemberSettings, getStoredMemberSettings } from "@utils/member-settings";
import { Banner, BeforeInstallPromptEvent, InfoResponse } from "kz-ui-sdk";

const persistContactBanners = (contactBanners: Banner[] | null) => {
  if (contactBanners) {
    localStorage.setItem(STORAGE_KEY.PUBLIC_CONTACT_BANNERS, JSON.stringify(contactBanners));
  } else {
    localStorage.removeItem(STORAGE_KEY.PUBLIC_CONTACT_BANNERS);
  }
};

const persistGroupedContactIcon = (groupedContactIcon: string | null) => {
  if (groupedContactIcon) {
    localStorage.setItem(STORAGE_KEY.PUBLIC_GROUPED_CONTACT_ICON, groupedContactIcon);
  } else {
    localStorage.removeItem(STORAGE_KEY.PUBLIC_GROUPED_CONTACT_ICON);
  }
};

export interface PreferenceSliceState {
  serverInfo: InfoResponse | null;
  isDisplayAppInstaller: boolean;
  isAppInstallable: boolean;
  appInstallerPrompt: BeforeInstallPromptEvent | null;
  isLoading: boolean;
  memberSettings: MemberSettings;
  isWsConnected?: boolean;
  pendingDepositOrderRef: string | null;
  // Balance WS lock state
  isBalanceWsLocked?: boolean;
  hasPendingBalanceUpdate?: boolean;
  isDailyStreakModalOpen?: boolean;
  // MegaShare seen state
  seenGiverMegaShareTxId: string | null;
  // SecretCode seen state
  seenGiverSecretCodeCode: string | null;
  // Topup success in session state
  hasTopUpSuccessInSession: boolean;
  // FTD incentive balls were granted for a deposit in this session (not persisted)
  hasFtdFreeBallsPopupPending: boolean;
}

// Initialize from localStorage (with TTL check)
const persistedDeposit = getPersistedDepositTracking();

const initialState: PreferenceSliceState = {
  serverInfo: null,
  isDisplayAppInstaller: false,
  isAppInstallable: false,
  appInstallerPrompt: null,
  isLoading: true,
  memberSettings: getStoredMemberSettings(),
  isWsConnected: false,
  pendingDepositOrderRef: persistedDeposit?.orderRef ?? null,
  // Balance WS lock default
  isBalanceWsLocked: false,
  hasPendingBalanceUpdate: false,
  isDailyStreakModalOpen: false,
  // Initialize from localStorage
  seenGiverMegaShareTxId: localStorage.getItem(STORAGE_KEY.MEGA_SHARE_SEEN_GIVER_TX_ID),
  seenGiverSecretCodeCode: localStorage.getItem(STORAGE_KEY.SECRET_CODE_SEEN_GIVER_CODE),
  // Topup success session state (not persisted)
  hasTopUpSuccessInSession: false,
  hasFtdFreeBallsPopupPending: false,
};

const preferenceSlice = createSlice({
  name: "@preference",
  initialState,
  reducers: {
    setServerInfo(state, action: PayloadAction<InfoResponse>) {
      state.serverInfo = action.payload;
      if (action.payload) {
        state.isLoading = false;
      }
      // Build & persist member settings from server info
      state.memberSettings = buildMemberSettings(action.payload);
      persistContactBanners(action.payload?.contactBanners ?? null);
      persistGroupedContactIcon(action.payload?.groupedContactIcon ?? null);
    },
    setDisplayAppInstaller(state, action: PayloadAction<boolean>) {
      state.isDisplayAppInstaller = action.payload;
    },
    setIsLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setIsWsConnected(state, action: PayloadAction<boolean>) {
      state.isWsConnected = action.payload;
    },
    setIsAppInstallable(state, action: PayloadAction<boolean>) {
      state.isAppInstallable = action.payload;
    },
    setAppInstallerPrompt(state, action: PayloadAction<BeforeInstallPromptEvent | null>) {
      state.appInstallerPrompt = action.payload;
    },
    setPendingDepositOrderRef(state, action: PayloadAction<string | null>) {
      state.pendingDepositOrderRef = action.payload;
      // Persist to sessionStorage with timestamp
      if (action.payload) {
        persistDepositTracking(action.payload, { startedAt: Date.now() });
      } else {
        clearDepositTracking();
      }
    },
    reset(state) {
      state.pendingDepositOrderRef = null;
      state.hasFtdFreeBallsPopupPending = false;
      clearDepositTracking();
    },
    // Balance WS lock reducers
    setBalanceWsLocked(state, action: PayloadAction<boolean>) {
      state.isBalanceWsLocked = action.payload;
      if (!action.payload) {
        // releasing the lock resets pending marker
        state.hasPendingBalanceUpdate = false;
      }
    },
    setHasPendingBalanceUpdate(state, action: PayloadAction<boolean>) {
      state.hasPendingBalanceUpdate = action.payload;
    },
    setDailyStreakModalOpen(state, action: PayloadAction<boolean>) {
      state.isDailyStreakModalOpen = action.payload;
    },
    setSeenGiverMegaShareTxId(state, action: PayloadAction<string | null>) {
      state.seenGiverMegaShareTxId = action.payload;
      // Persist to localStorage
      if (action.payload) {
        localStorage.setItem(STORAGE_KEY.MEGA_SHARE_SEEN_GIVER_TX_ID, action.payload);
      } else {
        localStorage.removeItem(STORAGE_KEY.MEGA_SHARE_SEEN_GIVER_TX_ID);
      }
    },
    setSeenGiverSecretCodeCode(state, action: PayloadAction<string | null>) {
      state.seenGiverSecretCodeCode = action.payload;
      // Persist to localStorage
      if (action.payload) {
        localStorage.setItem(STORAGE_KEY.SECRET_CODE_SEEN_GIVER_CODE, action.payload);
      } else {
        localStorage.removeItem(STORAGE_KEY.SECRET_CODE_SEEN_GIVER_CODE);
      }
    },
    setHasTopUpSuccessInSession(state, action: PayloadAction<boolean>) {
      state.hasTopUpSuccessInSession = action.payload;
    },
    setHasFtdFreeBallsPopupPending(state, action: PayloadAction<boolean>) {
      state.hasFtdFreeBallsPopupPending = action.payload;
    },
  },
});

export const {
  setServerInfo,
  setDisplayAppInstaller,
  setIsLoading,
  setIsWsConnected,
  setBalanceWsLocked,
  setHasPendingBalanceUpdate,
  setDailyStreakModalOpen,
  setSeenGiverMegaShareTxId,
  setSeenGiverSecretCodeCode,
  setHasTopUpSuccessInSession,
  setHasFtdFreeBallsPopupPending,
} = preferenceSlice.actions;

export default preferenceSlice;
